// SWR hooks for API data fetching
import useSWR from 'swr';
import { apiService, Product, ProductListResponse, Order, OrderListResponse, Delivery, SupportConversation, SupportMessage } from '../services/api';

// 通用fetcher函数
const fetcher = (url: string) => fetch(url).then(res => res.json());

// 商品相关hooks
export const useProducts = (page = 0, size = 10) => {
  const { data, error, isLoading, mutate } = useSWR<ProductListResponse>(
    `/api/products?page=${page}&size=${size}`,
    () => apiService.getProducts(page, size),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // 1分钟内去重
    }
  );

  return {
    products: data?.products || [],
    totalElements: data?.totalElements || 0,
    totalPages: data?.totalPages || 0,
    currentPage: data?.currentPage || page,
    isLoading,
    error,
    mutate,
  };
};

export const useProduct = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR(
    id ? `/api/products/${id}` : null,
    () => apiService.getProduct(id),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    product: data?.product,
    isLoading,
    error,
    mutate,
  };
};

export const useSearchProducts = (keyword: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    keyword ? `/api/products/search?q=${encodeURIComponent(keyword)}` : null,
    () => apiService.searchProducts(keyword),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    products: data?.products || [],
    isLoading,
    error,
    mutate,
  };
};

export const useProductsByPriceRange = (minPrice: number, maxPrice: number) => {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/products/price-range?min=${minPrice}&max=${maxPrice}`,
    () => apiService.getProductsByPriceRange(minPrice, maxPrice),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    products: data?.products || [],
    isLoading,
    error,
    mutate,
  };
};

export const useProductCategories = () => {
  const { data, error, isLoading, mutate } = useSWR(
    '/api/products/categories',
    () => apiService.getProductCategories(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 300000, // 5分钟内去重
    }
  );

  return {
    categories: data?.categories || {},
    isLoading,
    error,
    mutate,
  };
};

// 订单相关hooks
export const useUserOrders = (page = 0, size = 10) => {
  const { data, error, isLoading, mutate } = useSWR<OrderListResponse>(
    `/api/orders/user?page=${page}&size=${size}`,
    () => apiService.getUserOrders(page, size),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    orders: data?.orders || [],
    totalElements: data?.totalElements || 0,
    totalPages: data?.totalPages || 0,
    currentPage: data?.currentPage || page,
    isLoading,
    error,
    mutate,
  };
};

export const useOrder = (orderId: number) => {
  const { data, error, isLoading, mutate } = useSWR(
    orderId ? `/api/orders/${orderId}` : null,
    () => apiService.getOrder(orderId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    order: data?.order,
    isLoading,
    error,
    mutate,
  };
};

export const useOrderByNumber = (orderNumber: string) => {
  const { data, error, isLoading, mutate } = useSWR(
    orderNumber ? `/api/orders/number/${orderNumber}` : null,
    () => apiService.getOrderByNumber(orderNumber),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    order: data?.order,
    isLoading,
    error,
    mutate,
  };
};

// 配送相关hooks
export const useDeliveryByOrderId = (orderId: number) => {
  const { data, error, isLoading, mutate } = useSWR<Delivery>(
    orderId ? `/api/deliveries/order/${orderId}` : null,
    () => apiService.getDeliveryByOrderId(orderId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    delivery: data,
    isLoading,
    error,
    mutate,
  };
};

export const useDeliveryByTrackingNumber = (trackingNumber: string) => {
  const { data, error, isLoading, mutate } = useSWR<Delivery>(
    trackingNumber ? `/api/deliveries/tracking/${trackingNumber}` : null,
    () => apiService.getDeliveryByTrackingNumber(trackingNumber),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    delivery: data,
    isLoading,
    error,
    mutate,
  };
};

export const useUserDeliveries = (userId: number) => {
  const { data, error, isLoading, mutate } = useSWR<Delivery[]>(
    userId ? `/api/deliveries/user/${userId}` : null,
    () => apiService.getUserDeliveries(userId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    deliveries: data || [],
    isLoading,
    error,
    mutate,
  };
};

// 客服相关hooks
export const useUserConversations = (userId: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupportConversation[]>(
    userId ? `/api/support/users/${userId}/conversations` : null,
    () => apiService.getUserConversations(userId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    conversations: data || [],
    isLoading,
    error,
    mutate,
  };
};

export const useConversationMessages = (conversationId: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupportMessage[]>(
    conversationId ? `/api/support/conversations/${conversationId}/messages` : null,
    () => apiService.getConversationMessages(conversationId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: 5000, // 5秒自动刷新消息
    }
  );

  return {
    messages: data || [],
    isLoading,
    error,
    mutate,
  };
};

// 支付相关hooks
export const usePaymentStatus = (orderId: number) => {
  const { data, error, isLoading, mutate } = useSWR(
    orderId ? `/api/payments/order/${orderId}` : null,
    () => apiService.queryPaymentStatus(orderId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: 10000, // 10秒自动刷新支付状态
    }
  );

  return {
    paymentStatus: data?.status,
    isLoading,
    error,
    mutate,
  };
};

// 通用mutate函数，用于手动刷新数据
export const useMutate = () => {
  const { mutate } = useSWR('/api/mutate', null, { revalidateOnMount: false });
  return mutate;
};
